/*
 * TAKEDA CONFIDENTIAL – Highly Restricted: Do not distribute without prior approval
 *
 * © Copyright (2026) Takeda. All Rights Reserved
 */

import { ParsedQuery } from 'query-string';

import type { Configuration, PopupRequest } from '@azure/msal-browser';

import {
  getDocCheckAccessToken, validateDocCheckAccessToken,
} from '../../modules/authentication/authentication.api';
import {
  CALCULATOR_PAGE, LOGIN_PAGE, SIGN_UP_PAGE, TAKEDA_ID_LOGIN_PAGE,
} from '../../modules/navigation/navigation.constants';
import { EnvConfig } from '../../services/env-config/env-config.service';
import { replaceUrl } from '../browser.domain';
import { getCountryConfiguration } from '../global-config.domain';
import { CountriesDataType } from '../locale-data.types';
import { StringIndexed } from '../shared.types';
import {
 DocCheckErrorType, ErrorTranslations, LoginType,
  TakedaIdErrorType,
} from './authentication.types';

export const getMsalConfig = (envConfig: EnvConfig): Configuration => ({
  auth: {
    clientId: envConfig.msalClientId,
    authority: envConfig.msalAuthority,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
});

export const getLoginRequest = (): PopupRequest => ({
  scopes: ['openid', 'profile', 'email'],
});

export const getEntraAccountUrl = (
  envConfig: EnvConfig,
  locale: string,
  path: string
): string => {
  return `${envConfig.entraAccountsBaseUrl}${locale}/${path}`;
};


export const getSupportedLocales = (countries: CountriesDataType) => {
  const supportedLocales = Object.keys(countries).reduce(
    (acc, country) => [
      ...acc,
      ...countries[country].locales.map((locale) =>
        mapI18nLocale(locale, country)
      ),
    ],
    [] as string[]
  );
  return supportedLocales;
};

export const mapI18nLocale = (language: string, country: string) => {
  return `${language}-${country}`;
};


export const replaceTakedaLoginUrl = () => {
  replaceUrl(`/#${TAKEDA_ID_LOGIN_PAGE}`);
};

export const getLoginTypesForCountry = (
  country: string,
  countryConfiguration: CountriesDataType
) => {
  const config = getCountryConfiguration(country, countryConfiguration);
  if (!config) return undefined;

  let loginTypes = config.authMethods?.map(({ id }) => id as LoginType);
  if (!loginTypes?.length) {
    loginTypes = [LoginType.Unauthenticated];
  }
  return loginTypes;
};

export const getClientIdForCountryAndLoginType = (
  country: string,
  countryConfiguration: CountriesDataType,
  loginType: LoginType
) => {
  const authMethods = getCountryConfiguration(
    country,
    countryConfiguration
  )?.authMethods;

  return authMethods?.find(({ id }) => (id as LoginType) === loginType)
    ?.clientId;
};

export const validateDocCheckLogin = async (
  country: string,
  countryConfiguration: CountriesDataType,
  authenticationCode?: string,
  accessToken?: string
) => {
  const clientId = getClientIdForCountryAndLoginType(
    country as string,
    countryConfiguration,
    LoginType.DocCheck
  );

  if (accessToken) {
    const { boolIsValid } = await validateDocCheckAccessToken(accessToken);

    return boolIsValid ? accessToken : undefined;
  } else if (authenticationCode && country && clientId) {
    const { access_token } = await getDocCheckAccessToken(
      authenticationCode,
      country,
      clientId
    );

    return access_token;
  } else {
    return undefined;
  }
};

export const getAuthRequiredNavigateTo = (hasUserAccount?: boolean) => {
  return !hasUserAccount ? LOGIN_PAGE : CALCULATOR_PAGE;
};

export const isDocCheckLoginType = (
  searchParams: ParsedQuery<string>,
  loginTypes?: LoginType[],
  docCheckAccessToken?: string
) => {
  return !!(
    loginTypes?.includes(LoginType.DocCheck) &&
    (searchParams.dc_timestamp ||
      docCheckAccessToken ||
      loginTypes?.length === 1)
  );
};

type AuthClaims = {
  country?: string;
  HCP?: boolean;
  customer_id?: string;
  digital_id?: string;
};

export const getAuthenticationError = (
  claims: AuthClaims | undefined,
  countryConfiguration: CountriesDataType,
  country: string
) => {
  if (!claims) return TakedaIdErrorType.RequestError;

  const userCountry = claims.country as string;
  const isHCP = claims.HCP;

  if (!countryConfiguration[userCountry]) {
    return TakedaIdErrorType.InvalidCountry;
  }
  if (country !== userCountry) {
    return TakedaIdErrorType.WrongCountry;
  }
  if (!isHCP) {
    return TakedaIdErrorType.InvalidUser;
  }
};

export const getAuthenticationErrorKeys = (
  loginType: LoginType
): StringIndexed<ErrorTranslations> => {
  return loginType === LoginType.TakedaId
    ? {
        [TakedaIdErrorType.InvalidCountry]: {
          contact: 'contact_us.technical_support.email',
          reason: 'authentication.country_error.reason',
        },
        [TakedaIdErrorType.WrongCountry]: {
          reason: 'authentication.wrong_country_error.reason',
        },
        [TakedaIdErrorType.InvalidUser]: {
          reason: 'authentication.invalid_user.reason',
        },
        [TakedaIdErrorType.RequestError]: {
          reason: 'authentication.login.error.unable_to_sign_in',
        },
      }
    : {
        [DocCheckErrorType.RequestError]: {
          reason: 'authentication.login.error.unable_to_sign_in',
        },
      };
};

export const isLoginTypeSupported = (
  loginType: LoginType,
  country: string,
  countryConfiguration: CountriesDataType
) => {
  return getLoginTypesForCountry(country, countryConfiguration)?.includes(
    loginType
  );
};

export const isAuthPage = (pathname: string) => {
  return pathname.includes(LOGIN_PAGE) || pathname.includes(SIGN_UP_PAGE);
};

export const getUserId = (claims: AuthClaims | undefined) => {
  return claims?.customer_id;
};

export const getTakedaId = (claims: AuthClaims | undefined) => {
  return claims?.digital_id;
};
